<template>
  <div class="notification">
    <div v-if="loading">
      <v-btn loading flat style="width: 100%;" />
    </div>
    <div
      v-else
      v-for="(notification, index) in sortedNotificationList"
      :key="index"
      outline
    >
      <v-layout
        v-on:mouseover="onMouseover(notification)"
        @click="onClickNotification(notification)"
        :class="{
          unreadInformationPage: isUnreadNotfication(notification),
          unreadTopPage: isUnreadTopPageNotification(notification),
          read: isReadNotfication(notification),
          oldNotification: isOldNotfication(notification),
          clickable: isClickableNotification(notification),
        }"
      >
        <v-flex xs2 mt-4 ml-4>
          <v-icon :color="getIconColor(notification.level)" align-center large>
            {{ getIconType(notification.level) }}
          </v-icon>
        </v-flex>
        <v-flex xs7 mt-4>
          <p
            v-html="getNotificationMessage(notification.message)"
            style="white-space: pre-line"
          />
        </v-flex>
        <v-flex xs2 mt-5>
          <p>{{ calculateTime(notification.startTime) }}</p>
        </v-flex>
      </v-layout>
    </div>
  </div>
</template>

<script>
const notificationLevel = {
  info: 'info',
  warning: 'warn',
  alert: 'alert',
}

import dayjs from 'dayjs'
import sortBy from 'lodash/sortBy'
import { NotificationRepository } from '@/models/Notification'

export default {
  name: 'NotificationList',
  props: {
    notificationList: {
      type: Array,
      required: true,
    },
    isNotificationTopPage: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      color: {
        system: '#DDDDDD',
        examination: '#DDDDDD',
      },
    }
  },
  computed: {
    sortedNotificationList() {
      return sortBy(this.notificationList, notification =>
        dayjs(notification.startTime).unix(),
      ).reverse()
    },
    newNotificationList() {
      return this.notificationList.filter(
        notification => notification.isRead === false,
      )
    },
  },
  methods: {
    isOldNotfication(notification) {
      if (this.isNotificationTopPage) return false
      return !this.newNotificationList.some(
        newNotification => newNotification.id === notification.id,
      )
    },
    isUnreadNotfication(notification) {
      if (this.isNotificationTopPage) return false
      if (this.isOldNotfication(notification)) return false
      return notification.isRead === false
    },
    isReadNotfication(notification) {
      if (this.isOldNotfication(notification)) return false
      return notification.isRead
    },
    isUnreadTopPageNotification(notification) {
      if (!this.isNotificationTopPage) return false
      return notification.isRead === false
    },
    isClickableNotification(notification) {
      return notification.url
    },
    getIconType(level) {
      switch (level) {
        case notificationLevel.info:
          return 'info'
        case notificationLevel.warning:
        case notificationLevel.alert:
          return 'warning'
        default:
          return ''
      }
    },
    getIconColor(level) {
      switch (level) {
        case notificationLevel.info:
          return 'blue'
        case notificationLevel.warning:
          return 'red'
        case notificationLevel.alert:
          return 'yellow'
        default:
          return ''
      }
    },
    getNotificationMessage(message) {
      const regexp_url = /((h?)(ttps?:\/\/[a-zA-Z0-9.\-_@:/~?%&;=+#',()*!]+))/g
      const regexp_makeLink = function(url) {
        return '<a href=' + url + '>' + url + '</a>'
      }
      return message.replace(regexp_url, regexp_makeLink)
    },
    async onMouseover(notification) {
      if (notification.isRead) return
      const level = notification.level
      if (
        level === notificationLevel.info ||
        level === notificationLevel.warning
      ) {
        await this.readNotification(notification)
      }
    },
    async onClickNotification(notification) {
      if (!notification.isRead) {
        await this.readNotification(notification)
      }

      if (notification.url) {
        if (Object.prototype.hasOwnProperty.call(notification, 'url')) {
          window.open(notification.url)
        }
      }
    },
    async readNotification(notification) {
      notification.isRead = true
      await NotificationRepository.readNotification(notification.ids)
      this.$emit('fetchUnreadNotificationsCount')
    },
    calculateTime(startTime) {
      let hourOrDayOrMinute = '日前'
      const nowInDays = Date.now() / 86400000
      const startInDays = Date.parse(startTime) / 86400000
      if (nowInDays - startInDays >= 1)
        return Math.floor(nowInDays - startInDays) + hourOrDayOrMinute

      hourOrDayOrMinute = '時間前'
      const nowInHours = Date.now() / 3600000
      const startInHours = Date.parse(startTime) / 3600000
      if (nowInHours - startInHours >= 1)
        return Math.floor(nowInHours - startInHours) + hourOrDayOrMinute

      hourOrDayOrMinute = '分前'
      const nowInMinutes = Date.now() / 60000
      const startInMinutes = Date.parse(startTime) / 60000
      if (nowInMinutes - startInMinutes >= 1)
        return Math.floor(nowInMinutes - startInMinutes) + hourOrDayOrMinute
    },
  },
}
</script>

<style scoped lang="scss">
.notification {
  width: 100%;
  background-color: white;
}
.unreadInformationPage {
  background-color: #bad3ff;
}
.oldNotification {
  background-color: white;
}
.read {
  background-color: #cccccc;
}
.unreadTopPage {
  background-color: white;
}
.clickable {
  cursor: pointer;
}
</style>
